import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { selfServiceHomePath, useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  getMyPolicies,
  acknowledgePolicy,
  getMyVaultDocuments
} from '../../services/api/implementation/impl';

/**
 * What I have to read, and what the company has made available to me.
 *
 * Outstanding items come first, because this is a to-do before it is a library.
 * Everything here is resolved from the audience at read time, so somebody who
 * joined last week sees policies published last year.
 */
const MyPolicies = () => {
  const { onBack } = useSmartBack({ fallback: selfServiceHomePath() });
  const queryClient = useQueryClient();
  const [reading, setReading] = useState(null);

  const policies = useQuery({ queryKey: ['myPolicies'], queryFn: getMyPolicies });
  const vault = useQuery({ queryKey: ['myVault'], queryFn: getMyVaultDocuments });

  const accept = useMutation({
    // The VERSION id, not the policy id — accepting the wording that was on
    // screen, not whatever is current when the request lands.
    mutationFn: (versionId) => acknowledgePolicy({ policy_version_id: versionId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myPolicies'] });
      setReading(null);
      toast.success('Acknowledged');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not record that')
  });

  const rows = policies.data?.data ?? [];
  const meta = policies.data?.meta ?? {};
  const documents = vault.data?.data ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Policies & Documents' }} />

      <div className="space-y-4 p-4">
        {meta.outstanding > 0 && (
          <div className="flex items-start gap-2 rounded-control border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>
              {meta.outstanding === 1
                ? 'One policy is waiting for you to read and accept it.'
                : `${meta.outstanding} policies are waiting for you to read and accept them.`}
            </span>
          </div>
        )}

        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Policies
          </h2>
          {policies.isFetching ? (
            <Loader />
          ) : rows.length === 0 ? (
            <EmptyCard text="No policies have been circulated to you." />
          ) : (
            <div className="space-y-2">
              {rows.map((policy) => (
                <button
                  key={policy.id}
                  type="button"
                  onClick={() => setReading(policy)}
                  className={`flex w-full items-start gap-3 rounded-lg border bg-white p-4 text-left transition-colors hover:border-brand-600 ${
                    policy.outstanding ? 'border-warning-border' : 'border-gray-200'
                  }`}
                >
                  <FileText
                    size={18}
                    className={policy.outstanding ? 'mt-0.5 text-warning-fg' : 'mt-0.5 text-gray-400'}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900">{policy.title}</div>
                    <div className="text-xs text-gray-500">
                      {titleCase(policy.category)} · v{policy.version?.version_no}
                      {policy.version?.effective_from
                        ? ` · effective ${shortDate(policy.version.effective_from)}`
                        : ''}
                    </div>
                    {policy.summary && (
                      <p className="mt-1 text-sm text-gray-600">{policy.summary}</p>
                    )}
                    {/* A revision is not a new policy, and saying so is what
                        stops somebody skimming past a change to something they
                        have already read. */}
                    {policy.version?.version_no > 1 && policy.version?.change_note && (
                      <p className="mt-1 text-xs text-gray-500">
                        Changed: {policy.version.change_note}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    {policy.acknowledged_at ? (
                      <span className="flex items-center gap-1 text-xs text-success-fg">
                        <CheckCircle2 size={13} /> Accepted
                      </span>
                    ) : policy.requires_acknowledgement ? (
                      <span className="rounded bg-warning-bg px-2 py-0.5 text-[10px] font-semibold uppercase text-warning-fg">
                        To accept
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Read</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Company documents
          </h2>
          {vault.isFetching ? (
            <Loader />
          ) : documents.length === 0 ? (
            <EmptyCard text="No documents have been shared with you." />
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {documents.map((doc) => (
                <a
                  key={doc.id}
                  href={doc.file?.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:border-brand-600"
                >
                  <FileText size={16} className="mt-0.5 shrink-0 text-gray-400" />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-gray-900">{doc.title}</div>
                    {doc.category && (
                      <div className="text-xs text-gray-500">{doc.category}</div>
                    )}
                    {doc.description && (
                      <p className="mt-0.5 text-xs text-gray-600">{doc.description}</p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>
      </div>

      {reading && (
        <PolicyReader
          policy={reading}
          busy={accept.isPending}
          onClose={() => setReading(null)}
          onAccept={() => accept.mutate(reading.version.id)}
        />
      )}
    </Layout>
  );
};

const PolicyReader = ({ policy, busy, onClose, onAccept }) => (
  <Modal name={policy.title} onClose={onClose} width="720px">
    <div className="space-y-3">
      <div className="text-xs text-gray-500">
        Version {policy.version?.version_no}
        {policy.version?.effective_from
          ? ` · effective ${shortDate(policy.version.effective_from)}`
          : ''}
      </div>

      {policy.version?.body && (
        <div className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-relaxed text-gray-800">
          {policy.version.body}
        </div>
      )}

      {policy.version?.file?.url && (
        <a
          href={policy.version.file.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-brand-700 hover:underline"
        >
          <FileText size={14} /> Open the attached document
        </a>
      )}

      {policy.acknowledged_at ? (
        <p className="rounded bg-success-bg px-3 py-2 text-sm text-success-fg">
          You accepted this version on {shortDate(policy.acknowledged_at)}.
        </p>
      ) : policy.requires_acknowledgement ? (
        <>
          {/* Worded precisely. This records that a logged-in person clicked
              accept at a time — it is evidence of circulation, not a digital
              signature, and the button will not imply otherwise. */}
          <p className="text-xs text-gray-500">
            Accepting records that you, signed in as yourself, read this version
            on today&apos;s date.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
            >
              Not yet
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onAccept}
              className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {busy ? 'Recording…' : 'I have read and accept this'}
            </button>
          </div>
        </>
      ) : (
        <p className="text-xs text-gray-500">
          This policy is for information. No acknowledgement is required.
        </p>
      )}
    </div>
  </Modal>
);

const EmptyCard = ({ text }) => (
  <div className="rounded-lg border border-dashed border-gray-300 bg-white py-10 text-center text-sm text-gray-500">
    {text}
  </div>
);

function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function titleCase(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default MyPolicies;
